import type { LandingPage } from '@/features/landing/types'
import { breadcrumbLabel, getBasePageBySlug, landingPath } from '@/features/landing/registry'
import {
  breadcrumbJsonLd,
  buildPageHead,
  collectionPageJsonLd,
  getSiteOrigin,
  jsonLdScript,
  markdownAlternate,
  type HeadResult,
} from './siteMeta'

/**
 * Head builders for the SEO landing pages (`/pobyty` hub, `/pobyty/<slug>`
 * region/facility/category pages and nested `/pobyty/<region>/<dimension>`
 * combos). Shared by the runtime routes and the build-time prerender script —
 * single source of truth for their title/description and JSON-LD. Unlike the
 * `/ubytovani/hledat` search page, each landing page's canonical points at
 * itself, so they are indexed independently.
 */

const LANDING_BASE_PATH = '/pobyty'

export function buildLandingHead(page: LandingPage, options: { noindex?: boolean; origin?: string } = {}): HeadResult {
  const { noindex = false, origin = getSiteOrigin() } = options
  const path = landingPath(page)
  const url = `${origin}${path}`
  const base = buildPageHead({
    title: page.metaTitle,
    description: page.perex,
    path,
    origin,
    noindex,
  })

  // A page kept out of the index gets no JSON-LD either — a CollectionPage
  // entity for a page Google must not index is only a mixed signal.
  if (noindex) return base

  const crumbs = [
    { name: 'Domů', item: origin },
    { name: 'Pobyty', item: `${origin}${LANDING_BASE_PATH}` },
  ]
  // For combos, insert the region page as an intermediate crumb (region-first).
  if (page.kind === 'combo' && page.comboRegion) {
    const region = getBasePageBySlug(page.comboRegion)
    if (region) {
      crumbs.push({
        name: region.linkLabel,
        item: `${origin}${landingPath(region)}`,
      })
    }
  }
  crumbs.push({ name: breadcrumbLabel(page), item: url })

  return {
    ...base,
    links: [...(base.links ?? []), markdownAlternate(`${url}.md`)],
    scripts: [jsonLdScript(breadcrumbJsonLd(crumbs)), jsonLdScript(collectionPageJsonLd(page.h1, page.perex, url))],
  }
}

export function buildLandingIndexHead(origin: string = getSiteOrigin()): HeadResult {
  const url = `${origin}${LANDING_BASE_PATH}`
  const description =
    'Procházejte last-minute ubytování podle regionu, typu i vybavení – chalupy, chaty, glamping, pobyty se saunou, s bazénem nebo se psem. Vyberte si a vyrazte na poslední chvíli.'
  const base = buildPageHead({
    title: 'Tipy na ubytování podle regionu a vybavení | Mátevolno',
    description,
    path: LANDING_BASE_PATH,
    origin,
  })

  return {
    ...base,
    links: [...(base.links ?? []), markdownAlternate(`${url}.md`)],
    scripts: [
      jsonLdScript(
        breadcrumbJsonLd([
          { name: 'Domů', item: origin },
          { name: 'Pobyty', item: url },
        ]),
      ),
    ],
  }
}
